import { useReducer, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useMyProfile,
  useUpdateProfile,
  useUploadProfileImage,
  useDeleteProfileImage,
} from "../hooks/profileQueries";
import {
  profileReducer,
  initialProfileState,
  profileActions,
} from "../hooks/profileReducer";

import Navbar from "../components/EditProfile/Navbar";
import ProfileSection from "../components/EditProfile/ProfileSection";
import BasicInfo from "../components/EditProfile/BasicInfo";
import WorkExperience from "../components/EditProfile/WorkExperience";
import Social from "../components/EditProfile/Social";
import EditProfileSkeleton from "../components/Loaders/EditProfileSkeleton";
import ErrorModal from "../components/Modal/ErrorModal";

export default function EditProfile() {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(profileReducer, initialProfileState);
  const {
    userData,
    originalData,
    changedFields,
    previewImage,
    isDirty,
    isSaving,
    saveSuccess,
    activeTab,
    error,
    fieldErrors,
  } = state;

  const { data: profileData, isLoading, error: fetchError } = useMyProfile();

  // --- Mutations ---
  const updateProfileMutation = useUpdateProfile({
    onMutate: () => dispatch(profileActions.setSaving(true)),
    onSuccess: (updatedProfile) => {
      dispatch(profileActions.mergeChangedFields(updatedProfile));
      dispatch(profileActions.setSaveSuccess(true));
      dispatch(profileActions.setSaving(false));
      setTimeout(() => dispatch(profileActions.setSaveSuccess(false)), 3000);
      setTimeout(() => {
        window.location.href = `/${userData.username}`;
      }, 300);
    },
    onError: (err) => {
      if (err.isValidationError && err.fieldErrors) {
        dispatch(profileActions.setFieldErrors(err.fieldErrors));
      } else {
        dispatch(profileActions.setError(err.message));
      }
      dispatch(profileActions.setSaving(false));
    },
  });

  const uploadImageMutation = useUploadProfileImage({
    onSuccess: (imageUrl) => {
      dispatch(profileActions.updateField("profileImage", imageUrl));
      dispatch(profileActions.setPreviewImage(imageUrl));
    },
    onError: (err) => {
      dispatch(profileActions.setError(`Upload failed: ${err.message}`));
    },
  });

  const deleteImageMutation = useDeleteProfileImage({
    onSuccess: () => {
      dispatch(profileActions.updateField("profileImage", null));
      dispatch(profileActions.setPreviewImage(null));
      dispatch(profileActions.setSaveSuccess(true));
      setTimeout(() => dispatch(profileActions.setSaveSuccess(false)), 3000);
    },
    onError: (err) => {
      dispatch(profileActions.setError(`Delete failed: ${err.message}`));
    },
  });

  // --- Effects ---
  useEffect(() => {
    if (profileData && !originalData) {
      dispatch(profileActions.setProfileData(profileData));
    }
  }, [profileData, originalData]);

  useEffect(() => {
    if (fetchError) {
      dispatch(profileActions.setError(`Failed to load: ${fetchError.message}`));
    }
  }, [fetchError]);

  useEffect(() => {
    return () => {
      if (previewImage?.startsWith?.("blob:")) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  // --- Handlers ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      dispatch(profileActions.updateNestedField(name, value));
    } else {
      dispatch(profileActions.updateField(name, value));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      dispatch(profileActions.setError("Please select a valid image file"));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      dispatch(profileActions.setError("Image must be less than 10MB"));
      return;
    }

    const tempUrl = URL.createObjectURL(file);
    dispatch(profileActions.setTempImage(tempUrl));
    dispatch(profileActions.setPendingImageFile(file));
  };

  const handleRemoveImage = async () => {
    // Check if user has a pending image file (temporary, not yet uploaded)
    if (userData.pendingImageFile) {
      dispatch(profileActions.setPreviewImage(null));
      dispatch(profileActions.setPendingImageFile(null));
      dispatch(profileActions.setSaveSuccess(true));
      setTimeout(() => dispatch(profileActions.setSaveSuccess(false)), 3000);
    } else if (previewImage || userData.profileImage) {
      await deleteImageMutation.mutateAsync();
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const hasPendingImage = !!userData.pendingImageFile;
    const hasFieldChanges = Object.keys(changedFields).length > 0;

    if (!hasFieldChanges && !hasPendingImage) return;

    try {
      dispatch(profileActions.setSaving(true));
      let currentChanges = { ...changedFields };

      // 1. Upload Image if pending
      if (userData.pendingImageFile) {
        const imageUrl = await uploadImageMutation.mutateAsync(userData.pendingImageFile);
        currentChanges.profileImage = imageUrl;
        dispatch(profileActions.setPendingImageFile(null));
      }

      // 2. Update Profile if other fields changed
      const otherFields = Object.keys(currentChanges).filter(k => k !== "profileImage");

      if (otherFields.length > 0) {
        await updateProfileMutation.mutateAsync({
          changedFields: currentChanges,
          fullUserData: userData,
        });
      } else {
        // Image only success path
        dispatch(profileActions.setSaving(false));
        dispatch(profileActions.setSaveSuccess(true));
        setTimeout(() => dispatch(profileActions.setSaveSuccess(false)), 3000);
        setTimeout(() => {
          window.location.href = `/${userData.username}`;
        }, 300);
      }
    } catch (err) {
      dispatch(profileActions.setSaving(false));
    }
  };

  // --- UI Helpers ---
  const tabs = [
    { id: "profile", label: "Profile" },
    { id: "about", label: "About" },
    { id: "links", label: "Links" },
  ];

  const renderTabContent = () => {
    const props = { userData, onChange: handleChange, fieldErrors };
    switch (activeTab) {
      case "about": return <WorkExperience {...props} />;
      case "links": return <Social {...props} />;
      default: return <BasicInfo {...props} originalUsername={originalData?.username} />;
    }
  };

  if (isLoading) return <EditProfileSkeleton />;

  const isProcessing = isSaving || updateProfileMutation.isPending || uploadImageMutation.isPending || deleteImageMutation.isPending;
  const canSave = (isDirty || !!userData.pendingImageFile) && !isProcessing;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar
        canSave={canSave}
        userData={userData}
        handleSubmit={handleSubmit}
        isProcessing={isProcessing}
        saveSuccess={saveSuccess}
      />

      {/* Error */}
      <ErrorModal
        isOpen={!!error}
        message={error}
        onClose={() => dispatch(profileActions.clearError())}
      />

      <div className="pt-16 sm:pt-24 pb-12 px-3 sm:px-8">
        <div className="max-w-5xl mx-auto">
          <ProfileSection
            userData={userData}
            previewImage={previewImage}
            onImageUpload={handleImageUpload}
            onRemoveImage={handleRemoveImage}
            isUploadingImage={uploadImageMutation.isPending}
            isRemovingImage={deleteImageMutation.isPending}
          />

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 bg-white sticky top-[64px] z-20">
            <div className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => dispatch(profileActions.setActiveTab(tab.id))}
                  className={`
                    flex-1 flex items-center justify-center gap-1.5 py-4 px-2 
                    text-sm font-medium transition-all duration-200 border-b-2
                    ${activeTab === tab.id
                      ? "border-black text-black"
                      : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200"
                    }
                  `}
                >
                  <span className="uppercase tracking-wider">
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.label.split(" ")[0]}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {renderTabContent()}
          </form>
        </div>
      </div>
    </div>
  );
}